// Minimal HLS/CMAF VOD reader — no live playlists, byte-range segments, or encryption.

const resolveUrl = (base, relative) => new URL(relative, base).toString();

const fetchText = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch playlist (${response.status}): ${url}`);
  return response.text();
};

const parseMediaPlaylist = (text, baseUrl) => {
  const lines = text.split('\n').map((line) => line.trim());
  let initUrl = null;
  let programDateTime = null;
  const segments = [];
  let cumulative = 0;
  let pendingDuration = null;

  lines.forEach((line) => {
    if (line.startsWith('#EXT-X-MAP:')) {
      const match = line.match(/URI="([^"]+)"/);
      if (match) initUrl = resolveUrl(baseUrl, match[1]);
    } else if (line.startsWith('#EXT-X-PROGRAM-DATE-TIME:')) {
      // First one only — it's the wall-clock time of this playlist's first segment.
      if (programDateTime == null) {
        const parsed = Date.parse(line.slice('#EXT-X-PROGRAM-DATE-TIME:'.length));
        if (!Number.isNaN(parsed)) programDateTime = Math.floor(parsed / 1000);
      }
    } else if (line.startsWith('#EXTINF:')) {
      pendingDuration = parseFloat(line.slice('#EXTINF:'.length));
    } else if (line && !line.startsWith('#')) {
      const duration = pendingDuration ?? 0;
      segments.push({ url: resolveUrl(baseUrl, line), start: cumulative, duration });
      cumulative += duration;
      pendingDuration = null;
    }
  });

  return { initUrl, segments, totalDuration: cumulative, programDateTime };
};

const fetchMediaPlaylist = async (url) => parseMediaPlaylist(await fetchText(url), url);

const parseMasterPlaylist = (text, baseUrl) => {
  const lines = text.split('\n').map((line) => line.trim());
  const videoVariants = [];
  let audioUrl = null;
  let pendingStreamInf = null;

  lines.forEach((line) => {
    if (line.startsWith('#EXT-X-MEDIA:') && line.includes('TYPE=AUDIO')) {
      const match = line.match(/URI="([^"]+)"/);
      if (match && !audioUrl) audioUrl = resolveUrl(baseUrl, match[1]);
    } else if (line.startsWith('#EXT-X-STREAM-INF:')) {
      const bandwidthMatch = line.match(/BANDWIDTH=(\d+)/);
      const resolutionMatch = line.match(/RESOLUTION=(\d+)x(\d+)/);
      pendingStreamInf = {
        bandwidth: bandwidthMatch ? Number(bandwidthMatch[1]) : 0,
        width: resolutionMatch ? Number(resolutionMatch[1]) : null,
        height: resolutionMatch ? Number(resolutionMatch[2]) : null,
      };
    } else if (line && !line.startsWith('#') && pendingStreamInf) {
      videoVariants.push({ ...pendingStreamInf, url: resolveUrl(baseUrl, line) });
      pendingStreamInf = null;
    }
  });

  return { videoVariants, audioUrl };
};

const fetchMasterPlaylist = async (url) => parseMasterPlaylist(await fetchText(url), url);

// CMAF segments overlapping [startSeconds, endSeconds] can be safely byte-concatenated.
const fetchSegmentRange = async ({ mediaPlaylist, startSeconds, endSeconds }) => {
  const { initUrl, segments } = mediaPlaylist;
  const needed = segments.filter((segment) => segment.start + segment.duration > startSeconds && segment.start < endSeconds);
  if (!needed.length) throw new Error('Requested clip range is outside the available video.');

  const urls = [initUrl, ...needed.map((segment) => segment.url)].filter(Boolean);
  const buffers = await Promise.all(
    urls.map(async (url) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch video segment (${response.status}): ${url}`);
      return new Uint8Array(await response.arrayBuffer());
    })
  );

  const totalBytes = buffers.reduce((sum, buffer) => sum + buffer.length, 0);
  const combined = new Uint8Array(totalBytes);
  let offset = 0;
  buffers.forEach((buffer) => {
    combined.set(buffer, offset);
    offset += buffer.length;
  });

  return { bytes: combined, rangeStart: needed[0].start, rangeEnd: needed[needed.length - 1].start + needed[needed.length - 1].duration };
};

// Wall-clock time of video position 0, from the stream's PROGRAM-DATE-TIME tag — lets
// timestamp-to-seconds math work per event without a calibration constant for each lead-in.
const fetchStreamStartTimestamp = async (masterPlaylistUrl) => {
  const master = await fetchMasterPlaylist(masterPlaylistUrl);
  const mediaUrl = master.audioUrl ?? master.videoVariants[0]?.url;
  if (!mediaUrl) return null;
  const media = await fetchMediaPlaylist(mediaUrl);
  return media.programDateTime;
};

export { fetchMasterPlaylist, fetchMediaPlaylist, fetchSegmentRange, fetchStreamStartTimestamp };
